import { Link } from 'react-router-dom'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import { useLocale, localizedHref } from '../i18n/locale'

const PATH = '/kanban-board/'

interface Feature { icon: string; title: string; body: string }
interface Step { n: string; title: string; body: string }

interface KanbanContent {
  title: string
  desc: string
  crumb: string
  badge: string
  h1a: string
  h1b: string
  lead: string
  ctaOpen: string
  ctaHow: string
  checks: string[]
  featuresH2: string
  features: Feature[]
  stepsH2: string
  steps: Step[]
  privacyH2: string
  privacyBody: string
  privacyCta: string
  faqs: QA[]
  disclaimer: string
}

const EN: KanbanContent = {
  title: 'Free Kanban Board — No Signup, Unlimited, Private | RenoSheets',
  desc: 'A genuinely free kanban board. No signup, no limits, no ads in your way. Four-column workflow, colour-coded cards, due dates, and backup/restore — all stored privately in your own browser. Works offline.',
  crumb: 'Free Kanban Board',
  badge: 'Free · No signup · Private to your browser',
  h1a: 'A free kanban board for project management,',
  h1b: 'with no catch.',
  lead: 'Plan any project on a simple four-column board. No account, no limits, no data leaving your device.',
  ctaOpen: 'Start a new project planner →',
  ctaHow: 'How it works',
  checks: ['Free forever', 'No account needed', 'Unlimited boards & cards', 'Works offline'],
  featuresH2: 'Everything you need, nothing you don’t',
  features: [
    { icon: '🟰', title: 'One-tap column moves', body: 'Use the ← → arrows on each card to step it through Planning, Quoted, In Progress and Done — works on desktop or touch.' },
    { icon: '🎨', title: 'Colour-coded cards', body: 'Tag any card with a colour to group work by trade, owner, priority — however you like.' },
    { icon: '📅', title: 'Due dates', body: 'Add a due date to any card and see at-a-glance “overdue” and “due soon” badges.' },
    { icon: '♾️', title: 'No limits', body: 'Unlimited boards and unlimited cards. No paywall, no “upgrade for more columns”.' },
    { icon: '🔒', title: 'Private by design', body: 'Everything is saved in your browser only. No account, no server, nothing sent to us.' },
    { icon: '📦', title: 'Backup & restore', body: 'Export your whole board to a file and import it on any device — your data, your control.' },
    { icon: '📴', title: 'Works offline', body: 'Once the page has loaded it keeps working with no connection — handy on a jobsite.' },
    { icon: '🧱', title: 'Starter templates', body: 'Begin from a ready-made board (or a blank one) and rename everything to fit your project.' },
  ],
  stepsH2: 'Start in seconds',
  steps: [
    { n: '1', title: 'Open the board', body: 'Hit the button below — no email, no password, no setup screen. You’re straight in.' },
    { n: '2', title: 'Add your cards', body: 'Create a board, add cards to each column, set colours and due dates, and tap the card arrows to move them as work progresses.' },
    { n: '3', title: 'Keep it safe', body: 'Export a backup file anytime to keep your board or move it to another device.' },
  ],
  privacyH2: 'Your board never leaves your device',
  privacyBody: 'There’s no account and no server database. Your boards and cards are stored locally in your browser, so they stay private. Want a copy or to move to another device? Export a backup file in one click.',
  privacyCta: 'Open the free kanban board',
  faqs: [
    { q: 'Is there a truly free kanban board with no sign-up?', a: 'Yes. The RenoSheets kanban board is free with no account, no email, and no credit card. You open the page and start adding cards immediately. There is no paid tier and no trial that expires — the board is free to use as much as you like.' },
    { q: 'What is the best free kanban board for personal projects?', a: 'For a simple, private, no-signup board, RenoSheets works well: a clean four-column workflow (Planning → Quoted → In Progress → Done), colour-coded cards, due dates, unlimited boards and cards, and backup/restore — all stored in your own browser. It is ideal for one-person and small-team planning where you do not want to create yet another account.' },
    { q: 'Where is my kanban board data stored?', a: 'Entirely in your own web browser, using local storage on your device. Nothing is uploaded to a server and there is no account. That means your boards are private, but it also means they live on that one browser — so use the Export backup button to save a copy or move boards between devices.' },
    { q: 'Can I use the kanban board offline?', a: 'Yes. Because the board runs in your browser with no server calls, it keeps working without an internet connection once the page has loaded. This makes it useful on a jobsite or anywhere with spotty signal.' },
    { q: 'Is there a limit on the number of boards or cards?', a: 'No. You can create as many boards and cards as you want. There is no paywall, column cap, or card limit — the only practical limit is your browser’s local storage, which holds far more than a typical project needs.' },
    { q: 'Can I export or back up my kanban board?', a: 'Yes. Use Export backup to download a single file containing all your boards and cards, then Import backup on any browser or device to restore them. Matching items are updated and new ones are added, so it doubles as a way to move your boards around.' },
    { q: 'Do I need to install anything?', a: 'No. It runs in any modern web browser — there is no app to download and no extension to install. Open the page and the board is ready.' },
  ],
  disclaimer: 'The RenoSheets kanban board is a free planning tool. “Kanban” is a generic project-management term; RenoSheets is not affiliated with any kanban-branded product or certification.',
}

const FR: KanbanContent = {
  title: 'Tableau Kanban gratuit — sans inscription, illimité, privé | RenoSheets',
  desc: 'Un tableau Kanban vraiment gratuit. Sans inscription, sans limites, sans pubs dans votre chemin. Quatre colonnes de flux, cartes en couleur, dates d’échéance et sauvegarde/restauration — le tout stocké en privé dans votre navigateur. Fonctionne hors ligne.',
  crumb: 'Tableau Kanban gratuit',
  badge: 'Gratuit · Sans inscription · Privé dans votre navigateur',
  h1a: 'Un tableau Kanban gratuit pour la gestion de projet,',
  h1b: 'sans attrape.',
  lead: 'Planifiez tout projet sur un simple tableau à quatre colonnes. Aucun compte, aucune limite, vos données restent sur votre appareil.',
  ctaOpen: 'Démarrer un nouveau planificateur de projet →',
  ctaHow: 'Comment ça marche',
  checks: ['Gratuit pour toujours', 'Aucun compte requis', 'Tableaux et cartes illimités', 'Fonctionne hors ligne'],
  featuresH2: 'Tout ce qu’il vous faut, rien de superflu',
  features: [
    { icon: '🟰', title: 'Déplacement en un clic', body: 'Utilisez les flèches ← → sur chaque carte pour la faire passer entre Planification, Soumission, En cours et Terminé — souris ou tactile.' },
    { icon: '🎨', title: 'Cartes en couleur', body: 'Attribuez une couleur à toute carte pour regrouper le travail par métier, responsable ou priorité — comme vous voulez.' },
    { icon: '📅', title: 'Dates d’échéance', body: 'Ajoutez une échéance à toute carte et repérez d’un coup d’œil les étiquettes « en retard » et « bientôt dû ».' },
    { icon: '♾️', title: 'Aucune limite', body: 'Tableaux et cartes illimités. Aucun mur payant, aucun « passez à la version supérieure pour plus de colonnes ».' },
    { icon: '🔒', title: 'Privé par conception', body: 'Tout est enregistré dans votre navigateur seulement. Aucun compte, aucun serveur, rien ne nous est envoyé.' },
    { icon: '📦', title: 'Sauvegarde et restauration', body: 'Exportez tout votre tableau dans un fichier et réimportez-le sur tout appareil — vos données, votre contrôle.' },
    { icon: '📴', title: 'Fonctionne hors ligne', body: 'Une fois la page chargée, il continue de fonctionner sans connexion — pratique sur un chantier.' },
    { icon: '🧱', title: 'Modèles de départ', body: 'Partez d’un tableau prêt à l’emploi (ou vierge) et renommez tout selon votre projet.' },
  ],
  stepsH2: 'Démarrez en quelques secondes',
  steps: [
    { n: '1', title: 'Ouvrez le tableau', body: 'Cliquez le bouton ci-dessous — aucun courriel, aucun mot de passe, aucun écran de configuration. Vous y êtes directement.' },
    { n: '2', title: 'Ajoutez vos cartes', body: 'Créez un tableau, ajoutez des cartes à chaque colonne, choisissez couleurs et échéances, puis utilisez les flèches sur la carte pour la déplacer au fil du travail.' },
    { n: '3', title: 'Gardez-le en sécurité', body: 'Exportez un fichier de sauvegarde en tout temps pour conserver votre tableau ou le transférer ailleurs.' },
  ],
  privacyH2: 'Votre tableau ne quitte jamais votre appareil',
  privacyBody: 'Aucun compte, aucune base de données serveur. Vos tableaux et cartes sont stockés localement dans votre navigateur, donc ils restent privés. Vous voulez une copie ou changer d’appareil? Exportez un fichier de sauvegarde en un clic.',
  privacyCta: 'Ouvrir le tableau Kanban gratuit',
  faqs: [
    { q: 'Existe-t-il un tableau Kanban vraiment gratuit, sans inscription?', a: 'Oui. Le tableau Kanban de RenoSheets est gratuit, sans compte, sans courriel et sans carte de crédit. Vous ouvrez la page et ajoutez des cartes immédiatement. Il n’y a aucune version payante ni essai qui expire — le tableau est gratuit, autant que vous le voulez.' },
    { q: 'Quel est le meilleur tableau Kanban gratuit pour des projets personnels?', a: 'Pour un tableau simple, privé et sans inscription, RenoSheets convient bien : un flux clair à quatre colonnes (Planification → Soumission → En cours → Terminé), cartes en couleur, dates d’échéance, tableaux et cartes illimités, et sauvegarde/restauration — le tout dans votre navigateur. Idéal pour la planification individuelle ou en petite équipe, sans créer encore un autre compte.' },
    { q: 'Où sont stockées les données de mon tableau Kanban?', a: 'Entièrement dans votre propre navigateur, via le stockage local de votre appareil. Rien n’est téléversé sur un serveur et il n’y a aucun compte. Vos tableaux restent donc privés, mais ils vivent sur ce navigateur — utilisez le bouton Exporter la sauvegarde pour en conserver une copie ou la transférer entre appareils.' },
    { q: 'Puis-je utiliser le tableau Kanban hors ligne?', a: 'Oui. Comme le tableau fonctionne dans votre navigateur sans appels serveur, il continue de fonctionner sans connexion Internet une fois la page chargée. Pratique sur un chantier ou là où le signal est faible.' },
    { q: 'Y a-t-il une limite au nombre de tableaux ou de cartes?', a: 'Non. Vous pouvez créer autant de tableaux et de cartes que vous voulez. Aucun mur payant, aucune limite de colonnes ou de cartes — la seule limite pratique est le stockage local de votre navigateur, bien plus grand qu’un projet typique en a besoin.' },
    { q: 'Puis-je exporter ou sauvegarder mon tableau Kanban?', a: 'Oui. Utilisez Exporter la sauvegarde pour télécharger un seul fichier contenant tous vos tableaux et cartes, puis Importer la sauvegarde sur tout navigateur ou appareil pour les restaurer. Les éléments correspondants sont mis à jour et les nouveaux sont ajoutés, ce qui sert aussi à déplacer vos tableaux.' },
    { q: 'Dois-je installer quelque chose?', a: 'Non. Il fonctionne dans tout navigateur moderne — aucune application à télécharger, aucune extension à installer. Ouvrez la page et le tableau est prêt.' },
  ],
  disclaimer: 'Le tableau Kanban de RenoSheets est un outil de planification gratuit. « Kanban » est un terme générique de gestion de projet; RenoSheets n’est affilié à aucun produit ou certification de marque Kanban.',
}

export default function KanbanBoard() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <Seo title={c.title} description={c.desc} path={PATH} schema={softwareAppSchema('RenoSheets Free Kanban Board', c.desc, localizedHref(PATH, locale))} />
      <Breadcrumbs items={[{ name: locale === 'fr' ? 'Accueil' : 'Home', href: L('/') }, { name: c.crumb, href: L(PATH) }]} />

      {/* Hero */}
      <section className="text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand-tint/40 px-4 py-1.5 text-sm text-brand-text">
          <span className="h-1.5 w-1.5 rounded-full bg-brand"></span>
          {c.badge}
        </div>
        <h1 className="text-4xl font-bold leading-tight text-text md:text-5xl">
          {c.h1a}<br />
          <span className="text-brand-text">{c.h1b}</span>
        </h1>
        <p className="mt-4 text-lg text-text-muted">{c.lead}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to={L('/my-sheets/')} className="rounded-lg bg-primary px-6 py-3 text-base font-semibold text-on-primary shadow-md transition-opacity hover:opacity-90">
            {c.ctaOpen}
          </Link>
          <Link to={L('/kanban-board/how-it-works/')} className="rounded-lg border-2 border-brand px-6 py-3 text-base font-semibold text-brand-text transition-colors hover:bg-brand-tint">
            {c.ctaHow}
          </Link>
        </div>
        <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm text-text-subtle">
          {c.checks.map((label) => <span key={label}>✓ {label}</span>)}
        </div>
      </section>

      {/* Features */}
      <section className="mt-16">
        <h2 className="text-center text-3xl">{c.featuresH2}</h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {c.features.map((f) => (
            <div key={f.title} className="rounded-xl border border-border bg-surface p-5">
              <div className="text-2xl">{f.icon}</div>
              <h3 className="mt-2 font-semibold text-text">{f.title}</h3>
              <p className="mt-1.5 text-sm text-text-muted">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="mt-16">
        <h2 className="text-center text-3xl">{c.stepsH2}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-3">
          {c.steps.map((s) => (
            <div key={s.n} className="rounded-xl border border-border bg-surface-2 p-6">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand text-base font-bold text-white">{s.n}</div>
              <h3 className="mt-3 font-semibold text-text">{s.title}</h3>
              <p className="mt-1.5 text-sm text-text-muted">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Privacy callout */}
      <section className="mt-16 rounded-2xl border border-border bg-brand-tint/30 p-8 text-center">
        <h2 className="text-2xl font-semibold text-text">{c.privacyH2}</h2>
        <p className="mx-auto mt-3 max-w-2xl text-text-muted">{c.privacyBody}</p>
        <Link to={L('/my-sheets/')} className="mt-6 inline-block rounded-lg bg-primary px-6 py-3 font-semibold text-on-primary hover:opacity-90">
          {c.privacyCta}
        </Link>
      </section>

      <Faq items={c.faqs} />

      <div className="mx-auto mt-10 max-w-content border-t border-border pt-4 text-xs text-text-subtle">
        {c.disclaimer}
      </div>
    </div>
  )
}
